import gsap from "gsap";

export default class SwitchTabs {
    constructor(container = null) {
        if (!container) {
            this.initAllTabs();
            return;
        }

        this.container = container;
        this.tabsNav = this.container.querySelector('.tabs_nav');
        this.tabsNavItems = this.container.querySelectorAll('.tabs_nav-item');
        this.tabsContentPanels = this.container.querySelectorAll('.tabs_content-panel');
        this.isBlockSwitch2 = this.container.classList.contains('block-switch-2');
        this.backdrop = null;
        this.activeItem = null;
        this.hoverTimeline = null;
        this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        this.isInitialLoad = true;

        this.init();
    }

    initAllTabs() {
        document.querySelectorAll('.block-switch').forEach(block => {
            new SwitchTabs(block);
        });
    }

    init() {
        this.tabsNavItems.forEach(item => {
            if (this.isBlockSwitch2) {
                if (!this.isTouchDevice) {
                    item.addEventListener('mouseenter', () => this.handleMouseEnter(item));
                    item.addEventListener('mouseleave', () => this.handleMouseLeave(item));
                }
            }
            item.addEventListener('click', (e) => {
                e.preventDefault();
                this.handleTabClick(item);
            });
        });

        if (this.isBlockSwitch2) {
            this.setupBackdrop();
        }

        if (this.tabsNavItems.length > 0) {
            if (this.isBlockSwitch2 && this.backdrop) {
                const firstItem = this.tabsNavItems[0];
                firstItem.classList.add('active');
                this.activeItem = firstItem;

                this.setBackdropPositionWithoutAnimation(firstItem);

                setTimeout(() => {
                    gsap.to(this.backdrop, {
                        autoAlpha: 1,
                        duration: 0.3
                    });
                }, 100);

                const targetTab = firstItem.getAttribute('data-tab');
                const targetPanel = this.container.querySelector(`.tabs_content-panel[data-tab="${targetTab}"]`);
                if (targetPanel) {
                    targetPanel.classList.add('active');
                    gsap.to(targetPanel, {
                        opacity: 1,
                        duration: 0.5,
                        ease: "power2.inOut"
                    });
                }
            } else {
                this.tabsNavItems[0].click();
            }

            this.isInitialLoad = false;
        }
    }

    setupBackdrop() {
        this.backdrop = document.createElement('div');
        this.backdrop.className = 'tabs_nav-backdrop';
        this.tabsNav.appendChild(this.backdrop);

        gsap.set(this.backdrop, {
            position: 'absolute',
            backgroundColor: '#C3C3C3',
            borderRadius: '500px',
            zIndex: 1,
            top: '10px',
            left: 0,
            width: 0,
            height: 0
        });

        gsap.set(this.tabsNav, { position: 'relative' });

        this.tabsNavItems.forEach(item => {
            gsap.set(item, { position: 'relative', zIndex: 2 });
        });
    }

    setBackdropPositionWithoutAnimation(item) {
        const rect = item.getBoundingClientRect();
        const navRect = this.tabsNav.getBoundingClientRect();
        const offsetLeft = rect.left - navRect.left + this.tabsNav.scrollLeft;

        gsap.set(this.backdrop, {
            x: offsetLeft,
            width: rect.width,
            height: rect.height,
            borderRadius: "500px"
        });
    }

    handleTabClick(clickedItem) {
        const targetTab = clickedItem.getAttribute('data-tab');

        this.tabsNavItems.forEach(navItem => navItem.classList.remove('active'));

        this.tabsContentPanels.forEach(panel => {
            panel.classList.remove('active');
            gsap.to(panel, {
                opacity: 0,
                duration: 0.5,
                ease: "power2.inOut"
            });
        });

        clickedItem.classList.add('active');
        this.activeItem = clickedItem;

        if (this.isBlockSwitch2 && this.backdrop) {
            if (this.hoverTimeline) {
                this.hoverTimeline.kill();
                this.hoverTimeline = null;
            }
            if (this.isInitialLoad) {
                this.setBackdropPositionWithoutAnimation(clickedItem);
            } else {
                this.animateBackdrop(clickedItem);
            }
        }

        const targetPanel = this.container.querySelector(`.tabs_content-panel[data-tab="${targetTab}"]`);
        targetPanel.classList.add('active');
        gsap.to(targetPanel, {
            opacity: 1,
            duration: 0.5,
            ease: "power2.inOut"
        });
    }

    handleMouseEnter(hoveredItem) {
        if (this.isTouchDevice) return;
        if (hoveredItem === this.activeItem) return;

        if (this.hoverTimeline) {
            this.hoverTimeline.kill();
        }

        this.hoverTimeline = gsap.timeline();

        const rect = hoveredItem.getBoundingClientRect();
        const navRect = this.tabsNav.getBoundingClientRect();
        const offsetLeft = rect.left - navRect.left + this.tabsNav.scrollLeft;

        this.hoverTimeline.to(this.backdrop, {
            x: offsetLeft,
            width: rect.width,
            height: rect.height,
            duration: 0.3,
            ease: "back.out(1.4)",
            borderRadius: "500px"
        });
    }

    handleMouseLeave(hoveredItem) {
        if (this.isTouchDevice) return;
        if (this.activeItem && hoveredItem !== this.activeItem) {
            if (this.hoverTimeline) {
                this.hoverTimeline.kill();
            }

            this.hoverTimeline = gsap.timeline();

            const rect = this.activeItem.getBoundingClientRect();
            const navRect = this.tabsNav.getBoundingClientRect();
            const offsetLeft = rect.left - navRect.left + this.tabsNav.scrollLeft;

            this.hoverTimeline.to(this.backdrop, {
                x: offsetLeft,
                width: rect.width,
                height: rect.height,
                duration: 0.4,
                ease: "power2.out",
                borderRadius: "500px"
            });
        }
    }

    animateBackdrop(clickedItem) {
        const rect = clickedItem.getBoundingClientRect();
        const navRect = this.tabsNav.getBoundingClientRect();

        const offsetLeft = rect.left - navRect.left + this.tabsNav.scrollLeft;

        gsap.timeline()
            .to(this.backdrop, {
                x: offsetLeft,
                width: rect.width,
                height: rect.height,
                duration: 0.4,
                ease: "back.out(1.7)",
                borderRadius: "500px"
            })
            .to(this.backdrop, {
                scaleX: 1.05,
                // scaleY: 0.95,
                duration: 0.1,
                ease: "sine.out",
            })
            .to(this.backdrop, {
                scaleX: 1,
                // scaleY: 1,
                duration: 0.2,
                ease: "elastic.out(1, 0.3)",
            });
    }
}